import { ITEMS, type ItemId } from '@/data/items';
import type { Inventory } from '@/world/inventory';
import { BAG_SIZE, type QuickBag } from '@/world/quickBag';

/**
 * The bag, rearrangeable.
 *
 * Tapping an item opens what can be done with it. The default view is simply
 * what you are carrying — a backpack should read as an inventory first and a
 * puzzle of gestures second — and every action is named on a button rather
 * than being a gesture you have to already know.
 *
 * Two ways to move a stack, because neither alone is enough on a phone:
 *
 * - **Tap to lift, tap to place.** Always works, never fights the scroller,
 *   and is the only route that is reachable one-handed. This is the primary
 *   mechanism; drag is the flourish on top.
 * - **Press and hold to drag.** The panel body scrolls with `touch-action:
 *   pan-y`, so a drag that begins the moment a finger moves is indistinguishable
 *   from a scroll and the browser wins. Waiting for a stationary hold tells the
 *   two apart without guessing. A mouse has no such conflict, so it drags on
 *   movement alone.
 *
 * A stack can also be put on the quick-access bar from here. The bar can
 * assign from its own empty slots, but nobody finds that by staring at their
 * backpack, so the route exists in both directions and each slot says whether
 * it is already on the bar.
 *
 * Nothing in here destroys anything. A stack released anywhere that is not a
 * slot goes back where it came from, and Drop puts it on the ground at Katie's
 * feet where it can be picked up again — dropping that deleted things would be
 * deletion wearing a friendlier word.
 *
 * Tapping in here never changes which slot is *held* — the bar along the bottom
 * of the screen does that. This screen is for arranging, and one gesture that
 * sometimes rearranges and sometimes re-equips is how the old click-then-click
 * ended up feeling broken: it only ever moved things from the bag into the
 * hotbar, and silently did nothing in every other direction.
 */

/** A stationary hold this long turns a touch into a drag rather than a scroll. */
const HOLD_TO_DRAG_MS = 220;

/** Movement beyond this before the hold elapses is a scroll, not a drag. */
const DRAG_SLOP_PX = 10;

export interface BagGridOptions {
  readonly inventory: Inventory;
  /** The quick-access bar, so a stack can be sent to it from in here. */
  readonly quickBag: QuickBag;
  /** Persist. Called after any change to the bag. */
  readonly onChanged: () => void;
  readonly onMessage: (message: string) => void;
  /** Put a backpack slot's stack on the ground. Returns false if there is
      nowhere to put it. */
  readonly onDrop: (index: number) => boolean;
}

interface DragState {
  readonly pointerId: number;
  readonly index: number;
  readonly startX: number;
  readonly startY: number;
  holdTimer: number | null;
  active: boolean;
  ghost: HTMLElement | null;
}

export class BagGrid {
  private readonly grid = document.createElement('div');
  private readonly hint = document.createElement('p');
  private readonly picker = document.createElement('div');
  private readonly actions = document.createElement('div');
  private lifted: number | null = null;
  private actionsFor: number | null = null;
  private drag: DragState | null = null;

  constructor(private readonly options: BagGridOptions) {
    this.grid.className = 'gear-slots';
    this.grid.addEventListener('pointerdown', this.onPointerDown);

    this.hint.className = 'gear-hint';
    this.picker.className = 'bag-picker';
    this.picker.hidden = true;

    this.actions.className = 'bag-actions';
    this.actions.hidden = true;
  }

  /** Build into a container. The panel wipes its body on every tab change. */
  mount(container: HTMLElement): void {
    this.lifted = null;
    this.actionsFor = null;
    container.append(this.hint, this.grid, this.actions, this.picker);
    this.refresh();
  }

  destroy(): void {
    this.cancelDrag();
    this.grid.removeEventListener('pointerdown', this.onPointerDown);
    this.detachDragListeners();
  }

  private refresh(): void {
    const { inventory } = this.options;

    this.hint.textContent =
      this.lifted === null
        ? `${inventory.capacity} slots. Tap an item for what you can do with it; ` +
          'a dot means it is also on the bar.'
        : 'Tap another slot to place it, or the same slot to put it back.';

    this.renderActions();
    this.grid.replaceChildren();

    for (let index = 0; index < inventory.capacity; index++) {
      this.grid.appendChild(this.createSlot(index));
    }
  }

  private createSlot(index: number): HTMLButtonElement {
    const { inventory } = this.options;
    const slot = inventory.slotAt(index);

    const button = document.createElement('button');
    button.className = 'gear-slot';
    button.type = 'button';
    button.dataset.index = String(index);
    // Every slot is equal as storage; the marker only says whether this item
    // also has a place on the bar, which is otherwise invisible from in here.
    button.classList.toggle('is-empty', slot === null);
    button.classList.toggle('is-in-bag', slot !== null && this.bagSlotOf(slot.id) !== null);
    button.classList.toggle('is-lifted', index === this.lifted);
    button.classList.toggle('is-open', index === this.actionsFor);

    if (slot) {
      const definition = ITEMS[slot.id];
      button.style.setProperty('--tint', definition.tint);

      const label = document.createElement('span');
      label.className = 'gear-slot-label';
      label.textContent = definition.label;

      const count = document.createElement('span');
      count.className = 'gear-slot-count';
      count.textContent = definition.stackSize > 1 ? String(slot.count) : '';

      button.append(label, count);
      const onBar = this.bagSlotOf(slot.id);
      button.setAttribute(
        'aria-label',
        `${definition.name}, slot ${index + 1}` +
          (onBar === null ? '' : `, on the bar in slot ${onBar + 1}`) +
          (index === this.lifted ? ', lifted' : ''),
      );
    } else {
      button.setAttribute('aria-label', `Empty slot ${index + 1}`);
    }

    return button;
  }

  // --- Tapping -------------------------------------------------------------

  private handleTap(index: number): void {
    const { inventory } = this.options;

    if (this.lifted === null) {
      if (inventory.slotAt(index)) {
        this.actionsFor = this.actionsFor === index ? null : index;
        this.refresh();
      } else {
        this.openPicker(index);
      }
      return;
    }

    if (this.lifted === index) {
      this.lifted = null;
      this.refresh();
      return;
    }

    this.move(this.lifted, index);
  }

  private move(from: number, to: number): void {
    const changed = this.options.inventory.mergeOrSwap(from, to);
    this.lifted = null;
    this.actionsFor = null;
    if (changed) this.options.onChanged();
    this.refresh();
  }

  /** Which bar slot this item sits in, if any. */
  private bagSlotOf(id: ItemId): number | null {
    const { quickBag } = this.options;
    for (let i = 0; i < BAG_SIZE; i++) {
      if (quickBag.assignmentAt(i) === id) return i;
    }
    return null;
  }

  /**
   * What you can do with the tapped item.
   *
   * Named buttons rather than remembered gestures. Only actions that actually
   * do something appear: there is no Eat until food does anything to Katie.
   */
  private renderActions(): void {
    const { inventory, quickBag } = this.options;
    this.actions.replaceChildren();

    const index = this.actionsFor;
    const slot = index === null ? null : inventory.slotAt(index);
    if (index === null || !slot) {
      this.actions.hidden = true;
      return;
    }

    const definition = ITEMS[slot.id];
    const onBar = this.bagSlotOf(slot.id);

    const title = document.createElement('p');
    title.className = 'bag-action-title';
    title.textContent =
      definition.stackSize > 1 ? `${definition.name} x${slot.count}` : definition.name;
    this.actions.appendChild(title);

    const row = document.createElement('div');
    row.className = 'bag-action-row';

    row.appendChild(
      this.actionButton(onBar === null ? 'Put on the bar' : 'Take off the bar', () => {
        if (onBar !== null) {
          quickBag.assign(onBar, null);
          this.options.onMessage(`${definition.name} taken off the bar`);
        } else {
          const free = this.firstFreeBarSlot();
          if (free === null) {
            this.options.onMessage('The bar is full — take something off it first');
            return;
          }
          quickBag.assign(free, slot.id);
          this.options.onMessage(`${definition.name} is on the bar`);
        }
        this.actionsFor = null;
        this.options.onChanged();
        this.refresh();
      }),
    );

    row.appendChild(
      this.actionButton('Move', () => {
        this.lifted = index;
        this.actionsFor = null;
        this.refresh();
      }),
    );

    row.appendChild(
      this.actionButton('Drop', () => {
        if (!this.options.onDrop(index)) return;
        this.actionsFor = null;
        this.refresh();
      }),
    );

    row.appendChild(
      this.actionButton('Close', () => {
        this.actionsFor = null;
        this.refresh();
      }),
    );

    this.actions.append(row);
    this.actions.hidden = false;
  }

  private actionButton(label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'bag-action';
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private firstFreeBarSlot(): number | null {
    const { quickBag } = this.options;
    for (let i = 0; i < BAG_SIZE; i++) {
      if (quickBag.assignmentAt(i) === null) return i;
    }
    return null;
  }

  // --- The empty-slot picker ----------------------------------------------

  /**
   * Everything you own already lives in the bag, so an empty slot has nothing
   * new to offer — what it can do is take something from elsewhere in the bag.
   * That is what this lists.
   */
  private openPicker(target: number): void {
    const { inventory } = this.options;
    this.picker.replaceChildren();

    const filled: number[] = [];
    for (let i = 0; i < inventory.capacity; i++) {
      if (inventory.slotAt(i)) filled.push(i);
    }

    if (filled.length === 0) {
      this.options.onMessage('Your bag is empty');
      return;
    }

    const title = document.createElement('p');
    title.className = 'gear-hint';
    title.textContent = `Move into slot ${target + 1}`;
    this.picker.appendChild(title);

    const list = document.createElement('div');
    list.className = 'bag-picker-list';

    for (const index of filled) {
      const slot = inventory.slotAt(index)!;
      const definition = ITEMS[slot.id];

      const option = document.createElement('button');
      option.className = 'bag-picker-option';
      option.type = 'button';
      option.style.setProperty('--tint', definition.tint);

      const name = document.createElement('span');
      name.textContent = definition.name;

      const count = document.createElement('span');
      count.className = 'gear-slot-count';
      count.textContent = definition.stackSize > 1 ? `x${slot.count}` : '';

      option.append(name, count);
      option.addEventListener('click', () => {
        this.closePicker();
        this.move(index, target);
      });
      list.appendChild(option);
    }

    const cancel = document.createElement('button');
    cancel.className = 'bag-picker-cancel';
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => {
      this.closePicker();
    });

    this.picker.append(list, cancel);
    this.picker.hidden = false;
  }

  private closePicker(): void {
    this.picker.hidden = true;
    this.picker.replaceChildren();
  }

  // --- Dragging ------------------------------------------------------------

  private readonly onPointerDown = (event: PointerEvent): void => {
    const target = event.target instanceof Element ? event.target.closest('.gear-slot') : null;
    if (!(target instanceof HTMLElement)) return;

    const index = Number(target.dataset.index);
    if (!Number.isInteger(index)) return;

    // Nothing to drag out of an empty slot, but it is still a tap target.
    const draggable = this.options.inventory.slotAt(index) !== null;

    this.drag = {
      pointerId: event.pointerId,
      index,
      startX: event.clientX,
      startY: event.clientY,
      holdTimer: null,
      active: false,
      ghost: null,
    };

    // A mouse has no scroll gesture to be confused with, so it starts dragging
    // on movement alone (see onPointerMove) and needs no hold.
    if (draggable && event.pointerType !== 'mouse') {
      this.drag.holdTimer = window.setTimeout(() => {
        if (this.drag) this.beginDrag();
      }, HOLD_TO_DRAG_MS);
    }

    document.addEventListener('pointermove', this.onPointerMove);
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointercancel', this.onPointerCancel);
  };

  private beginDrag(): void {
    const drag = this.drag;
    if (!drag || drag.active) return;

    const slot = this.options.inventory.slotAt(drag.index);
    if (!slot) return;

    drag.active = true;
    this.lifted = drag.index;

    const definition = ITEMS[slot.id];
    const ghost = document.createElement('div');
    ghost.className = 'bag-ghost';
    ghost.textContent = definition.label;
    ghost.style.setProperty('--tint', definition.tint);
    ghost.style.transform = `translate(${drag.startX}px, ${drag.startY}px)`;
    document.body.appendChild(ghost);
    drag.ghost = ghost;

    this.refresh();
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const moved = Math.hypot(dx, dy);

    if (!drag.active) {
      if (moved <= DRAG_SLOP_PX) return;
      if (event.pointerType === 'mouse') {
        this.beginDrag();
      } else {
        // Moved before the hold elapsed: the player is scrolling the panel.
        // Stand down and let the browser have the gesture.
        this.cancelDrag();
      }
      if (!this.drag?.active) return;
    }

    event.preventDefault();
    if (drag.ghost) {
      drag.ghost.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`;
    }
    this.highlight(this.slotIndexAt(event.clientX, event.clientY));
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const wasActive = drag.active;
    const from = drag.index;
    const over = this.slotIndexAt(event.clientX, event.clientY);

    this.cancelDrag();

    if (!wasActive) {
      // A press that never became a drag is a tap, wherever it ended up.
      this.handleTap(from);
      return;
    }

    if (over !== null && over !== from) {
      this.move(from, over);
      return;
    }

    // Released on itself or outside the grid: it goes back to its slot. There
    // is nowhere else for it to go, and nothing here throws anything away.
    this.lifted = null;
    this.refresh();
  };

  private readonly onPointerCancel = (): void => {
    const wasActive = this.drag?.active ?? false;
    this.cancelDrag();
    if (wasActive) {
      this.lifted = null;
      this.refresh();
    }
  };

  private cancelDrag(): void {
    const drag = this.drag;
    if (drag && drag.holdTimer !== null) window.clearTimeout(drag.holdTimer);
    drag?.ghost?.remove();
    this.drag = null;
    this.highlight(null);
    this.detachDragListeners();
  }

  private detachDragListeners(): void {
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerCancel);
  }

  private slotIndexAt(x: number, y: number): number | null {
    const element = document.elementFromPoint(x, y);
    const slot = element instanceof Element ? element.closest('.gear-slot') : null;
    if (!(slot instanceof HTMLElement)) return null;
    const index = Number(slot.dataset.index);
    return Number.isInteger(index) ? index : null;
  }

  private highlight(index: number | null): void {
    for (const slot of this.grid.querySelectorAll('.gear-slot')) {
      slot.classList.toggle('is-drop-target', Number((slot as HTMLElement).dataset.index) === index);
    }
  }
}
